import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Check,
  ClipboardList,
  Copy,
  FileText,
  Pill,
  Star,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import styles from './PostConsultPage.module.css';

interface PostConsultState {
  patientName?: string;
  duration?: string;
  rating?: number;
  disposicion?: string;
  soap?: string;
}

export default function PostConsultPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = (location.state as PostConsultState) || {};

  const {
    patientName = 'Paciente',
    duration = '00:00',
    rating = 0,
    disposicion = 'completada',
    soap = '',
  } = state;

  const handleCopy = () => {
    if (soap) {
      navigator.clipboard.writeText(soap);
    }
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate('/salas')}
          >
            <ArrowLeft size={18} />
            Volver al dashboard
          </button>
        </div>
        <div className={styles.headerRight}>
          {rating > 0 && (
            <div className={styles.ratingDisplay}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  size={14}
                  className={n <= rating ? styles.starFilled : styles.starEmpty}
                />
              ))}
            </div>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate('/salas')}
          >
            Siguiente paciente
          </Button>
        </div>
      </header>

      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroIcon}>
          <Check size={32} />
        </div>
        <h1 className={styles.heroTitle}>Consulta finalizada.</h1>
        <p className={styles.heroSub}>
          {patientName} recibirá un resumen por correo electrónico.
        </p>
      </div>

      {/* Content Grid */}
      <div className={styles.grid}>
        {/* Clinical Summary */}
        <div className={`${styles.card} ${styles.cardFull}`}>
          <div className={styles.cardHead}>
            <div>
              <h2 className={styles.cardTitle}>Resumen clínico</h2>
              <span className={styles.cardSub}>Generado por Alma</span>
            </div>
            <div className={styles.cardActions}>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={handleCopy}
                aria-label="Copiar"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
          <div className={styles.cardBody}>
            {soap ? (
              <pre className={styles.soapText}>{soap}</pre>
            ) : (
              <p className={styles.placeholder}>
                No se generaron notas clínicas en esta consulta.
              </p>
            )}
          </div>
        </div>

        {/* Prescription */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>
              <Pill size={16} />
              Receta
            </h2>
          </div>
          <div className={styles.cardBody}>
            <p className={styles.placeholder}>Sin receta generada</p>
          </div>
        </div>

        {/* Orders */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>
              <FileText size={16} />
              Órdenes
            </h2>
          </div>
          <div className={styles.cardBody}>
            <p className={styles.placeholder}>Sin órdenes emitidas</p>
          </div>
        </div>

        {/* Next appointment */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>
              <Calendar size={16} />
              Próxima cita
            </h2>
          </div>
          <div className={styles.cardBody}>
            <p className={styles.placeholder}>Sin cita programada</p>
          </div>
        </div>

        {/* Stats */}
        <div className={`${styles.card} ${styles.cardFull}`}>
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <div className={styles.statNum}>{duration}</div>
              <div className={styles.statLabel}>duración</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statNum}>
                <Badge variant="success" size="sm">
                  {disposicion}
                </Badge>
              </div>
              <div className={styles.statLabel}>disposición</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statNum}>
                <ClipboardList size={18} />
              </div>
              <div className={styles.statLabel}>ID: {id}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
